// Reglas de visibilidad en la web. La API es la autoridad (filtra y responde 403/422);
// la web solo oculta lo que el rol no puede usar.
import type { AreaMargen, Rol } from '../api/tipos';

export const ROLES_PREVENTA: Rol[] = ['Preventa.Responsable', 'Preventa.Suplente'];
export const ROLES_FINANCIERO: Rol[] = ['Preventa.Responsable', 'Preventa.Suplente', 'Gerencia', 'Finanzas'];

export const tieneRol = (roles: readonly Rol[], ...buscados: Rol[]) => buscados.some((r) => roles.includes(r));
export const esPreventa = (roles: readonly Rol[]) => tieneRol(roles, ...ROLES_PREVENTA);
export const esGerencia = (roles: readonly Rol[]) => tieneRol(roles, 'Gerencia');
export const puedeOperar = (roles: readonly Rol[]) => esPreventa(roles) || esGerencia(roles);
export const veFinanciero = (roles: readonly Rol[]) => tieneRol(roles, ...ROLES_FINANCIERO);
export const puedeVerIndicadores = (roles: readonly Rol[]) => puedeOperar(roles);
/** Módulo comercial (§13): lo ven Comercial, Gerencia, preventa y Lector; lo editan Comercial y Gerencia. */
export const puedeEditarCRM = (roles: readonly Rol[]) => tieneRol(roles, 'Comercial', 'Gerencia');
export const puedeVerCRM = (roles: readonly Rol[]) => puedeEditarCRM(roles) || esPreventa(roles) || tieneRol(roles, 'Lector');
/** Compras y Abastecimiento (§14): gestionan Compras y Gerencia; Preventa y Comercial crean solicitudes y ven las suyas. */
export const puedeGestionarCompras = (roles: readonly Rol[]) => tieneRol(roles, 'Compras', 'Gerencia');
export const puedeVerCompras = (roles: readonly Rol[]) => puedeGestionarCompras(roles) || esPreventa(roles) || tieneRol(roles, 'Comercial', 'Finanzas');
/** Inteligencia corporativa completa de precios: Compras, Gerencia y Finanzas. */
export const veInteligenciaCompras = (roles: readonly Rol[]) => tieneRol(roles, 'Compras', 'Gerencia', 'Finanzas');
/** Precios de cotizaciones de proveedores (RULE-099): Compras y los roles financieros. */
export const vePreciosProveedor = (roles: readonly Rol[]) => veFinanciero(roles) || tieneRol(roles, 'Compras');
/** Personas, Gestión Humana y Vida Laboral (§15): administran Gestión Humana y Gerencia; cualquier rol válido ve el
 * organigrama, el directorio y su propio expediente (Mi Vida Laboral). */
export const puedeGestionarPersonas = (roles: readonly Rol[]) => tieneRol(roles, 'Personas.GestionHumana', 'Gerencia');
export const puedeVerPersonas = (roles: readonly Rol[]) => roles.length > 0;
/** Textos legales del ingreso (PER-12): redactan Gestión Humana, Gerencia y Jurídica; aprueban Jurídica y Gerencia. */
export const puedeRedactarTextos = (roles: readonly Rol[]) => tieneRol(roles, 'Personas.GestionHumana', 'Gerencia', 'Juridica');
export const puedeAprobarTextos = (roles: readonly Rol[]) => tieneRol(roles, 'Juridica', 'Gerencia');
export const puedeSubirCatalogo = (roles: readonly Rol[]) => tieneRol(roles, 'Gerencia', 'Preventa.Responsable');

const ROL_DE_AREA: Record<AreaMargen, Rol> = {
  compras: 'Compras',
  operaciones: 'Operaciones',
  finanzas: 'Finanzas',
  cartera: 'Cartera',
};
export const puedeAportarArea = (roles: readonly Rol[], area: AreaMargen) => roles.includes(ROL_DE_AREA[area]);

/**
 * Vocabulario de `acciones_permitidas` (backend/app/estados.py · acciones_permitidas()).
 * La web muestra un control solo si la API devuelve la acción correspondiente.
 */
export const ACCION = {
  editar: 'editar',
  procesar: 'procesar',
  subirArchivo: 'subir_archivo',
  registrarCasoNegocio: 'registrar_caso_negocio',
  crearOferta: 'crear_oferta',
  aprobarOferta: 'aprobar_oferta',
  solicitarAjustes: 'solicitar_ajustes',
  rechazarOferta: 'rechazar_oferta',
  noOferta: 'no_oferta',
  registrarResultado: 'registrar_resultado',
  resolverExcepcion: 'resolver_excepcion',
  margenConciliar: 'margen_real_conciliar',
  margenCerrar: 'margen_real_cerrar',
} as const;

export const accionCambiarEstado = (estado: string) => `cambiar_estado:${estado}`;
export const accionAporteMargen = (area: AreaMargen) => `margen_real_aporte:${area}`;
export const PREFIJO_CAMBIAR_ESTADO = 'cambiar_estado:';

export function tieneAccion(permitidas: readonly string[] | undefined, accion: string): boolean {
  return !!permitidas && permitidas.includes(accion);
}

/** Estados destino de las transiciones manuales permitidas (`cambiar_estado:<ESTADO>`). */
export function destinosManuales(permitidas: readonly string[] | undefined): string[] {
  return (permitidas ?? []).filter((a) => a.startsWith(PREFIJO_CAMBIAR_ESTADO)).map((a) => a.slice(PREFIJO_CAMBIAR_ESTADO.length));
}

/** Usuarios y permisos administrados en INNOVA (2.29): solo Gerencia. */
export const administraUsuarios = (roles: readonly Rol[]) => tieneRol(roles, 'Gerencia');
